import type {
  PhlebotomyOrder,
  PhlebotomyOrderCreatePayload,
  PhlebotomyOrderMarkCompletedPayload,
  PhlebotomyOrderPreviewData,
} from "@/types/phlebotomy-order";

import { requestData } from "@/api/api-config";

/**
 * Resolve the derived phlebotomy-order fields the compose form can't safely
 * derive itself (ordering-site location, clinic fax) so the preview renders
 * entirely client-side. Query params are snake_case — FastAPI does not
 * camel-convert query params. `catchError: false` so a failed lookup surfaces
 * to the caller (which closes the preview tab and toasts).
 */
export async function fetchPhlebotomyOrderPreviewData(
  patientId: string,
  orderingProviderId: string,
): Promise<PhlebotomyOrderPreviewData | null> {
  return await requestData<PhlebotomyOrderPreviewData>(
    "phlebotomy-orders/preview-data",
    "get",
    {
      searchParams: {
        patient_id: patientId,
        ordering_provider_id: orderingProviderId,
      },
    },
    false,
  );
}

/**
 * Create a phlebotomy order from the client-rendered PDF.
 *
 * The PDF is generated in the browser (the single source of truth shared
 * with the preview, byte-identical to the previewed bytes) and uploaded as a
 * multipart `pdf` part; the structured fields ride alongside as a JSON
 * `payload` part. `catchError: false` so a failed create surfaces to the
 * mutation's onError. Longer timeout than the default 20s — the multipart
 * upload carries the full rendered PDF, which can outrun the default on a
 * slow connection.
 */
export async function createPhlebotomyOrder(
  data: PhlebotomyOrderCreatePayload,
  pdf: Blob,
): Promise<PhlebotomyOrder | null> {
  const formData = new FormData();
  formData.append(
    "pdf",
    new File([pdf], "phlebotomy-order.pdf", { type: "application/pdf" }),
  );
  formData.append("payload", JSON.stringify(data));
  return await requestData<PhlebotomyOrder>(
    "phlebotomy-orders",
    "post",
    { body: formData },
    false,
    60000,
  );
}

/** All phlebotomy orders for a patient, newest first — backs the Sent /
 *  Completed status chips on the Documents tab. */
export async function fetchPhlebotomyOrders(
  patientId: string,
): Promise<PhlebotomyOrder[] | null> {
  return await requestData<PhlebotomyOrder[]>("phlebotomy-orders", "get", {
    searchParams: { patient_id: patientId },
  });
}

export async function markPhlebotomyOrderCompleted(
  orderId: string,
  data: PhlebotomyOrderMarkCompletedPayload,
): Promise<PhlebotomyOrder | null> {
  return await requestData<PhlebotomyOrder>(
    `phlebotomy-orders/${orderId}/completed`,
    "patch",
    { json: data },
    false,
  );
}
